import { 
    PitchData, ResultData, BattingStats, ScoringData 
}  from '../enums/index.js';
import {
    hardHitClassifier as defaultHardHitClassifier,
} from '../classificators/index.js';
import {
    classifiesAsHit as defaultHitClassifier,
    classifiesAsBattedBallEvent as defaultBattedBallEventClassifier,
    classifiesAsHomeRun as defaultHomeRunClassifier,
} from '../classificators/plateAppearanceResult/index.js';
import { ResultPair } from '../valueObjects/index.js';
import BattingStatsCalculator from './BattingStatsCalculator.js';
import PlateAppearanceResultToBaseNumberConverter from '../converters/PlateAppearanceResultToBaseNumberConverter.js';
import classifiesAsPitch from '../classificators/pitchCall/classifiesAsPitch.js';

/**
 * Calculates basic stats for hitting
 */
class UniversalBattingStatsCalculator extends BattingStatsCalculator
{
    constructor(
        plateAppearanceResultToBaseNrConverter, hitClassifier, hardHitClassifier, homeRunClassifier,
        battedBallEventClassifier, pitchesClassifier, oldDataFallbackCalculator
    ) {
        super(
            plateAppearanceResultToBaseNrConverter || PlateAppearanceResultToBaseNumberConverter,
            hitClassifier || defaultHitClassifier,
            hardHitClassifier || defaultHardHitClassifier,
            homeRunClassifier || defaultHomeRunClassifier,
            battedBallEventClassifier || defaultBattedBallEventClassifier
        );

        if (typeof(pitchesClassifier) !== 'undefined' && typeof(pitchesClassifier) !== 'function') {
            throw new TypeError(`pitchesClassifier must be an predicate taking single argument - pitch call. Pass undefined to use default implementation`);
        }

        if (typeof(oldDataFallbackCalculator) !== 'undefined' && !(oldDataFallbackCalculator instanceof BattingStatsCalculator)) {
            throw new TypeError(`oldDataFallbackCalculator must be an instance of BattingStatsCalculator class or undefined to use default implementation`);
        }

        this.oldDataFallbackCalculator = oldDataFallbackCalculator || new BattingStatsCalculator();
        this.pitchesClassifier = pitchesClassifier || classifiesAsPitch
    }

    calculateHitterStats(resultPairs, includePercentages) {
        if (!(resultPairs instanceof Array)) {
            throw new TypeError('results must be an instance of array');
        }

        if (typeof(includePercentages) === 'undefined') {
            includePercentages = true;
        }
        else if(typeof(includePercentages) !== 'boolean') {
            throw new TypeError('includePercentages must be an boolean type');
        }

        const battingStats = this.getEmptyStatsStructure();
        const battersPa = [];

        //let's go through results
        for (let i = 0; i < resultPairs.length; i++) {
            const resultPair = resultPairs[i];
            if (!(resultPair instanceof ResultPair)) {
                throw new TypeError(`Item at index ${i} is not an instance of ResultPair`);
            }

            const {pitch, hit} = resultPair;

            const batterSessionPaId = this.getBatterId(pitch, hit);

            if (pitch) {
                const pitchData = pitch[ResultData.Data.key];
                if (typeof(pitchData) !== 'object' || pitchData === null) {
                    throw new TypeError(`pitch.Data at index ${i} is not an object or null`);
                }

                const pitchResult = pitchData[PitchData.Result.key];
                const paResult = pitchData[ScoringData.PlateAppearanceResult.key];
                const pitchCall = pitchData[ScoringData.PitchCall.key];

                let totalBases = 0;
                if (paResult && pitchCall) {
                    if (!this.pitchesClassifier(pitchCall)) {
                        // no pitch case, balk, etc
                        continue;
                    }

                    totalBases = this.pitchResultToBaseNrConverter(paResult);
                    if (this.hitClassifier(paResult)) {
                        this.applyHitStats(battingStats, totalBases, hit);
                    }

                    if (this.homeRunClassifier(paResult)) {
                        battingStats[BattingStats.HomeRuns.key]++;
                    }

                    if (this.battedBallEventClassifier(paResult)) {
                        this.oldDataFallbackCalculator.applyFairBallStats(battingStats, hit);
                        battingStats[BattingStats.BattedBalls.key]++;
                    }
                }
                else {
                    totalBases = this.oldDataFallbackCalculator.pitchResultToBaseNrConverter(pitchResult);
                    if (this.oldDataFallbackCalculator.hitClassifier(pitchResult)) {
                        this.oldDataFallbackCalculator.applyHitStats(battingStats, totalBases, hit);
                    }

                    if (this.oldDataFallbackCalculator.homeRunClassifier(pitchResult)) {
                        battingStats[BattingStats.HomeRuns.key]++;
                    }

                    if (this.oldDataFallbackCalculator.battedBallEventClassifier(pitchResult)) {
                        this.oldDataFallbackCalculator.applyFairBallStats(battingStats, hit);
                        battingStats[BattingStats.BattedBalls.key]++;
                    }
                }

                battingStats[BattingStats.NumberOfPitches.key]++;
                battingStats[BattingStats.TotalBases.key] += totalBases;
            }
            else if (!hit) {
                throw new TypeError(`No pitch nor hit at index ${i}`);
            }

            // TODO - better plate appearance check
            if (battersPa.indexOf(batterSessionPaId) < 0) {
                battersPa.push(batterSessionPaId);
            }
        }

        battingStats[BattingStats.PlateAppearances.key] = battersPa.length;

        // TODO - not sure if we should use [BattingStats.Hits.key] here instead ? or maybe BBE ?
        if (battingStats.MeasuredHits) {
            battingStats[BattingStats.HardHitRate.key] = 100 * battingStats.HardHits / battingStats.MeasuredHits;
        }

        // TODO - not sure if we should use FairBalls here ?
        if (battingStats.MeasuredFairBalls) {
            // http://m.mlb.com/glossary/advanced-stats/ground-ball-rate
            battingStats[BattingStats.GroundBallRate.key] = 100 * battingStats.GroundBalls / battingStats.MeasuredFairBalls;

            // http://m.mlb.com/glossary/advanced-stats/pop-up-rate
            battingStats[BattingStats.PopUpRate.key] = 100 * battingStats.PopUps / battingStats.MeasuredFairBalls;

            // http://m.mlb.com/glossary/advanced-stats/line-drive-rate
            battingStats[BattingStats.LineDriveRate.key] = 100 * battingStats.LineDrives / battingStats.MeasuredFairBalls;

            // https://www.baseball-reference.com/bullpen/Fly_ball
            // http://m.mlb.com/glossary/advanced-stats/fly-ball-rate
            battingStats[BattingStats.FlyBallRate.key] = 100 * battingStats.FlyBalls / battingStats.MeasuredFairBalls;
        }

        // http://m.mlb.com/glossary/advanced-stats/pitches-per-plate-appearance
        if (battingStats[BattingStats.PlateAppearances.key]) {
            battingStats[BattingStats.PitchesPerPa.key] = battingStats[BattingStats.NumberOfPitches.key] / battingStats[BattingStats.PlateAppearances.key];
        }

        return battingStats;
    }
}

export default UniversalBattingStatsCalculator;